import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { MapHitEvent, MapInteractionsConfig } from '@gpx-spa/geo-domain';
import type { V2CurrentPositionLike } from '@/map/operational/v2-current-positions-markers';
import {
  buildConvoyLivePaintForShipments,
  convoyGroupHoverTrackers,
  convoyHitToggleShipmentId,
  convoyMarkerSurface,
  type ConvoyLivePaint,
  type ConvoyTrackerPresentation,
} from '@/map/operational/convoy';
import { allShipmentDevices } from '@/utils/shipment-detail-stops';
import { trackerLabelResolverFromSources } from '@/utils/named-tracker-set';
import type { NamedTracker } from '@/utils/named-tracker-set';

type ConvoyShipmentRef = {
  id: number;
  device_ids?: number[] | null;
  devices?: readonly NamedTracker[] | null;
};

export type UseConvoyLiveMapOptions = {
  shipments: () => readonly ConvoyShipmentRef[];
  positions: () => readonly V2CurrentPositionLike[];
  /** Extra presentation beyond labels (e.g. exception titles on detail map). */
  exceptionTitlesForDevice?: (deviceId: number) => readonly string[];
};

/**
 * Shared live-convoy UI state for list and detail maps: expand, paint, hit fragment, hover roster.
 */
export function useConvoyLiveMap(options: UseConvoyLiveMapOptions): {
  expandedShipmentIds: Ref<Set<number>>;
  toggleExpandedShipment: (shipmentId: number) => void;
  resetExpandedShipments: () => void;
  presentation: ComputedRef<ConvoyTrackerPresentation>;
  livePaint: ComputedRef<ConvoyLivePaint>;
  markers: ComputedRef<ConvoyLivePaint['markers']>;
  convoyInteractionFragment: ComputedRef<
    Pick<MapInteractionsConfig, 'triggers' | 'onHit' | 'surface'>
  >;
  groupHoverTrackersFromHit: (hit: MapHitEvent | null) => NamedTracker[] | null;
} {
  const expandedShipmentIds = ref(new Set<number>());

  function toggleExpandedShipment(shipmentId: number) {
    const next = new Set(expandedShipmentIds.value);
    if (next.has(shipmentId)) next.delete(shipmentId);
    else next.add(shipmentId);
    expandedShipmentIds.value = next;
  }

  function resetExpandedShipments() {
    expandedShipmentIds.value = new Set();
  }

  const presentation = computed<ConvoyTrackerPresentation>(() => {
    const label = trackerLabelResolverFromSources(
      options.shipments().flatMap((s) => allShipmentDevices(s as never)),
    );
    const exceptionTitles =
      options.exceptionTitlesForDevice ?? (() => [] as const);
    return {
      label,
      exceptionTitles: (deviceId: number) => exceptionTitles(deviceId),
    };
  });

  const livePaint = computed(() =>
    buildConvoyLivePaintForShipments(options.shipments(), options.positions(), {
      expandedShipmentIds: expandedShipmentIds.value,
      presentation: presentation.value,
    }),
  );

  const markers = computed(() => livePaint.value.markers);

  const convoyInteractionFragment = computed(() => ({
    triggers: ['click', 'hover'] as const,
    onHit(event: MapHitEvent) {
      const id = convoyHitToggleShipmentId(event);
      if (id != null) toggleExpandedShipment(id);
    },
    surface: convoyMarkerSurface,
  }));

  function groupHoverTrackersFromHit(
    hit: MapHitEvent | null,
  ): NamedTracker[] | null {
    if (!hit) return null;
    return convoyGroupHoverTrackers(hit, options.shipments());
  }

  return {
    expandedShipmentIds,
    toggleExpandedShipment,
    resetExpandedShipments,
    presentation,
    livePaint,
    markers,
    convoyInteractionFragment,
    groupHoverTrackersFromHit,
  };
}
